import { useT } from "@transifex/react";
import { debounce, isEqual, merge } from "lodash";
import { BVersion, Settings, SettingsSection, SettingsSubSection, TeacherTestDetails } from "p6m-p6u";
import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import PDFDownloadButton from "../../../basic/pdfBasic/PDFDownloadButton/PDFDownloadButton";
import PDFPreview from "../../../basic/pdfBasic/PDFPreview/PDFPreview";
import SyncStatus from "../../../basic/syncStatus/SyncStatus";
import { useTestCreationContext } from "../../../context/TestCreationContext";
import { TestVersionName } from "../../../enums/settings";
import { createBVersion } from "../../../helpers/ABVersionHelper";
import { putRequest } from "../../../helpers/networkHelper";
import { getStandardOrDefaultConfiguration } from "../../../helpers/PDFHelper";
import { handleMissingScores } from "../../../helpers/ScoringHelper";
import PDFSettingsSaveConfigurationButton from "./pdfSettingsSaveConfigurationButton/PDFSettingsSaveConfigurationButton";
import PDFGoodLuckSettings from "./pdfSettingsSections/pdfGoodLuckSettings/PDFGoodLuckSettings";
import PDFGradesSignaturesSettings from "./pdfSettingsSections/pdfGradesSignaturesSettings/PDFGradesSignaturesSettings";
import PDFHeaderSettings from "./pdfSettingsSections/pdfHeaderSettings/PDFHeaderSettings";
import PDFScoreSettings from "./pdfSettingsSections/pdfScoreSettings/PDFScoreSettings";
import PDFTitleSettings from "./pdfSettingsSections/pdfTitleSettings/PDFTitleSettings";
import { PDFConfigurationRef } from "../../../views/create/testPreview/TestPreview";

import {
    ABSwitch,
    ABSwitchButton,
    ABVersionSwitchWrapper,
    ButtonContainer,
    ConfigSection,
    Container,
    Header,
    PDFConfigurationWrapper,
    PreviewSection,
    StatusAndCloseWrapper,
} from "./StyledComponents";

export interface ChangeSettingsParams<T extends SettingsSection, K extends SettingsSubSection<T>> {
    sectionTitle: T;
    settingTitle: K;
    setting: {
        key: keyof Settings[T][K];
        value: Settings[T][K][keyof Settings[T][K]];
    };
}

const PDFConfiguration = forwardRef<PDFConfigurationRef>((_props, ref) => {
    const history = useHistory();

    useImperativeHandle(ref, () => ({
        handleClose,
    }));

    const {
        prepareFullTestDetails,
        testContent,
        setSettings,
        setShowScores,
        showScores,
        setTestContent,
        testName,
        setTestName,
        syncSharedTestCopy,
        // createPreviewData, // comment in while EDITING THE PDF
    } = useTestCreationContext();

    const [teacherTestDetails, setTeacherTestDetails] = useState<TeacherTestDetails | null>(null);
    const [isConfigSyncing, setIsConfigSyncing] = useState(false);
    const [isConfigSynced, setIsConfigSynced] = useState(true);

    const standardOrDefaultConfiguration = getStandardOrDefaultConfiguration();
    const showABGroup = teacherTestDetails
        ? teacherTestDetails?.settings
            ? teacherTestDetails.settings.header.abGroup.show
            : standardOrDefaultConfiguration.settings.header.abGroup.show
        : false;

    const [showBVersion, setShowBVersion] = useState(false);
    const testVersionName = showABGroup && showBVersion ? TestVersionName.B : TestVersionName.A;
    const [bVersionTestDetails, setBVersionTestDetails] = useState<TeacherTestDetails | null>(null);

    const t = useT();

    const t_exercisePDF = t("Exercise Sheet", { _tags: "CreateTest,PDFCreation" });
    const t_groupA = t("Group A (original)", { _tags: "PDFConfiguration" });
    const t_groupB = t("Group B", { _tags: "PDFConfiguration" });

    const t_test = t("Test", { _tags: "PDFConfiguration" });
    const t_testVersion = t("Test {version}", {
        version: testVersionName,
        _tags: "PDFConfiguration",
    });

    const t_solution = t("Solution", { _tags: "PDFConfiguration" });
    const t_solutionVersion = t("Solution {version}", {
        version: testVersionName,
        _tags: "PDFConfiguration",
    });

    const abVersionFileName = showABGroup ? `${testVersionName} ` : "";

    const availableButtons = [
        {
            text: showABGroup ? t_testVersion : t_test,
            isSolution: false,
            fileName: `${abVersionFileName}${teacherTestDetails?.name ?? t_exercisePDF} ${t_test}.pdf`,
            testDetails: showABGroup && showBVersion ? bVersionTestDetails : teacherTestDetails,
            secondary: false,
        },
        {
            text: showABGroup ? t_solutionVersion : t_solution,
            isSolution: true,
            fileName: `${abVersionFileName}${teacherTestDetails?.name ?? t_exercisePDF} ${t_solution}.pdf`,
            testDetails: showABGroup && showBVersion ? bVersionTestDetails : teacherTestDetails,
            secondary: true,
        },
    ];

    useEffect(() => {
        const getTestDetails = async () => {
            // comment out while EDITING THE PDF
            const currentTestDetails = { ...prepareFullTestDetails(testContent) };

            // comment in while EDITING THE PDF
            // let currentTestDetails = { ...prepareFullTestDetails(testContent) };
            // if (currentTestDetails && currentTestDetails.content && currentTestDetails.content.length > 0) {
            //     // save into localstorage for reload, overwrite if not present
            //     _saveTestInLocalStorage(currentTestDetails, createPreviewData);
            // } else {
            //     currentTestDetails = await _getCurrentTestFromLocalStorage();
            // }
            // / __________________________________________________________________________________________________

            if (!currentTestDetails || !currentTestDetails.content || currentTestDetails.content.length === 0) {
                //missing content, navigate back to home
                history.push("/assistent");
            }

            const mergedDetails = merge({}, standardOrDefaultConfiguration.settings, currentTestDetails.settings); // uses an empty object to merge without references
            setTeacherTestDetails({
                ...currentTestDetails,
                settings: mergedDetails,
                showScores: showScores,
            });
        };

        getTestDetails();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const saveTestInDatabase = async (
        currentTestDetails: TeacherTestDetails,
        updatedProperties: Partial<TeacherTestDetails>
    ) => {
        // teacherTestDetails needs to be passed as a prop because during re-render the useState gets reinitalised and teacherTestDetails would be undefined

        if (!currentTestDetails?.id) {
            // not available for anonymous users
            setIsConfigSyncing(false);
            setIsConfigSynced(true);
            return;
        }

        try {
            const testId = currentTestDetails.id;
            const combinedTestDetails = { ...currentTestDetails, ...updatedProperties };

            if (testId === combinedTestDetails.id) {
                await putRequest(`aea/tests/?testId=${testId}`, combinedTestDetails); // saves non-shortened testDetails (with both answers and questions)
                await syncSharedTestCopy(testId, combinedTestDetails);
                setIsConfigSynced(true);
            }

            setIsConfigSyncing(false);
        } catch (error) {
            setIsConfigSyncing(false);
            setTeacherTestDetails({ ...teacherTestDetails, name: testName });
            setTestName(currentTestDetails.name || "");
        }
    };

    const handleSave = async (
        currentTestDetails: TeacherTestDetails,
        updatedProperties: Partial<TeacherTestDetails>
    ) => {
        if (!updatedProperties) return;

        await saveTestInDatabase(currentTestDetails, updatedProperties);

        // comment in while EDITING THE PDF
        // _saveTestInLocalStorage(updatedProperties);
    };

    const saveSettingsDebounced = debounce(
        async (currentTestDetails: TeacherTestDetails, updatedProperties: Partial<TeacherTestDetails>) => {
            await handleSave(currentTestDetails, updatedProperties);
        },
        10000
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const saveSettingsMemorised = useMemo(() => saveSettingsDebounced, []); // prevents the debounce from being recreated on every render

    useEffect(() => {
        if (showABGroup) createBVersionFromA();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [teacherTestDetails?.content, teacherTestDetails?.settings, teacherTestDetails?.showScores]);

    const createBVersionFromA = (overwrite?: boolean) => {
        const bVersion: BVersion = createBVersion(teacherTestDetails ?? {}, overwrite);

        setBVersionTestDetails(bVersion.bVersionTestDetails);

        if (
            bVersion.shorthand.length > 0 &&
            !isEqual(bVersion.shorthand, teacherTestDetails?.settings?.header.abGroup.bVersion)
        ) {
            changeSettings({
                sectionTitle: "header",
                settingTitle: "abGroup",
                setting: { key: "bVersion", value: bVersion.shorthand },
            });
        }
    };

    const changeSettings = <T extends SettingsSection, K extends SettingsSubSection<T>>({
        sectionTitle,
        settingTitle,
        setting,
    }: ChangeSettingsParams<T, K>) => {
        if (!teacherTestDetails?.settings) return;

        const copiedTeacherTestDetails = { ...teacherTestDetails };

        const updatedPdfDetails = {
            ...(copiedTeacherTestDetails.settings as Settings),
            [sectionTitle]: {
                ...(copiedTeacherTestDetails.settings?.[sectionTitle] ?? {}),
                [settingTitle]: {
                    ...(copiedTeacherTestDetails.settings?.[sectionTitle]?.[settingTitle] ?? {}),
                    [setting.key]: setting.value,
                },
            },
        };

        copiedTeacherTestDetails.settings = updatedPdfDetails;
        setTeacherTestDetails(copiedTeacherTestDetails);
        setIsConfigSyncing(true);
        setIsConfigSynced(false);
        saveSettingsMemorised(teacherTestDetails, { settings: copiedTeacherTestDetails.settings });

        setSettings(updatedPdfDetails);
    };

    const handleClose = async () => {
        if (isConfigSyncing) {
            saveSettingsMemorised.cancel();

            if (teacherTestDetails?.settings) {
                await handleSave(teacherTestDetails, {
                    settings: teacherTestDetails.settings,
                    showScores: teacherTestDetails.showScores,
                });
            }
        }
    };

    const fixPDFScore = (): TeacherTestDetails => {
        let updatedTeacherTest = { ...teacherTestDetails };

        if (!updatedTeacherTest.showScores) {
            const newExercisesWithScores = handleMissingScores([...(updatedTeacherTest.content ?? [])]);

            updatedTeacherTest = {
                ...updatedTeacherTest,
                content: newExercisesWithScores ? newExercisesWithScores ?? [] : updatedTeacherTest.content,
            };
        }

        return updatedTeacherTest;
    };

    const onToggleScore = async () => {
        let updatedTeacherTest = fixPDFScore();

        updatedTeacherTest = {
            ...updatedTeacherTest,
            showScores: !updatedTeacherTest.showScores,
        };

        setTeacherTestDetails(updatedTeacherTest);
        setIsConfigSyncing(true);
        setIsConfigSynced(false);

        saveSettingsMemorised(updatedTeacherTest, {
            showScores: updatedTeacherTest.showScores,
            content: updatedTeacherTest.content,
        });

        setShowScores(updatedTeacherTest.showScores ?? showScores);
        setTestContent(updatedTeacherTest.content ?? testContent);
    };

    const handleSaveTitle = (newTitle: string) => {
        if (teacherTestDetails) {
            setIsConfigSyncing(true);
            setIsConfigSynced(false);
            saveSettingsMemorised(teacherTestDetails, { name: newTitle });
            setTeacherTestDetails({ ...teacherTestDetails, name: newTitle }); // needed to keep context in sync
            setTestName(newTitle || "");
        }
    };

    const renderABSwitch = () => {
        return (
            <ABVersionSwitchWrapper>
                <ABSwitch onClick={() => setShowBVersion(!showBVersion)}>
                    <ABSwitchButton isActive={!showBVersion}>{t_groupA}</ABSwitchButton>
                    <ABSwitchButton isActive={showBVersion}>{t_groupB}</ABSwitchButton>
                </ABSwitch>
            </ABVersionSwitchWrapper>
        );
    };

    return (
        <PDFConfigurationWrapper data-testid="pdf-configuration">
            <StatusAndCloseWrapper>
                <SyncStatus
                    isSyncing={isConfigSyncing}
                    isSynced={isConfigSynced}
                    isAbsolutePosition
                />
            </StatusAndCloseWrapper>
            <Header>
                <ButtonContainer>
                    {availableButtons.map((button, buttonIndex) => (
                        <PDFDownloadButton
                            key={buttonIndex}
                            buttonText={button.text}
                            isSolution={button.isSolution}
                            fileName={button.fileName}
                            testDetails={button.testDetails ?? {}}
                            secondary={button.secondary}
                            analyticsSource="pdf-configuration"
                        />
                    ))}
                </ButtonContainer>
            </Header>
            {teacherTestDetails && (
                <Container>
                    <PreviewSection>
                        {showABGroup && renderABSwitch()}
                        <PDFPreview
                            testDetails={
                                showABGroup && showBVersion && bVersionTestDetails
                                    ? bVersionTestDetails ?? {}
                                    : teacherTestDetails ?? {}
                            }
                        />
                    </PreviewSection>
                    <ConfigSection>
                        <PDFHeaderSettings
                            teacherTestDetails={teacherTestDetails}
                            changeSettings={changeSettings}
                            createBVersionFromA={createBVersionFromA}
                            setShowBVersion={setShowBVersion}
                        />
                        <PDFTitleSettings
                            changeSettings={changeSettings}
                            teacherTestDetails={teacherTestDetails}
                            onSaveTitle={handleSaveTitle}
                        />
                        <PDFScoreSettings
                            teacherTestDetails={teacherTestDetails}
                            changeSettings={changeSettings}
                            onToggleScore={onToggleScore}
                        />
                        <PDFGradesSignaturesSettings
                            teacherTestDetails={teacherTestDetails}
                            changeSettings={changeSettings}
                        />
                        <PDFGoodLuckSettings
                            teacherTestDetails={teacherTestDetails}
                            changeSettings={changeSettings}
                        />
                        <PDFSettingsSaveConfigurationButton teacherTestDetails={teacherTestDetails} />
                    </ConfigSection>
                </Container>
            )}
        </PDFConfigurationWrapper>
    );
});

export default PDFConfiguration;

/* comment in while EDITING THE PDF _____________________________________________________________________
 -> this will enable reloading the page by saving the data into the localstorage
 -> search for "EDITING THE PDF" to find all places in this file

 PLEASE NOTE: when this page is reloaded, the close button can break due to missing history / location
*/

// const _getCurrentTestFromLocalStorage = () => {
//     const storedTestDetails = localStorage.getItem("localFullPreviewData");

//     if (storedTestDetails) {
//         const parsedTestDetails = JSON.parse(storedTestDetails);
//         return parsedTestDetails;
//     }

//     return null;
// };

// const _updateLocalStorage = (
//     storageKey: string,
//     updatedProperties: Partial<TeacherTestDetails>,
//     overwriteData?: TeacherTestDetails
// ) => {
//     try {
//         const existingData = localStorage.getItem(storageKey);
//         if (existingData || overwriteData) {
//             console.log("saving into localstorage");
//             const existingTestDetails = overwriteData ?? JSON.parse(existingData ?? "");
//             const updatedData = {
//                 ...existingTestDetails,
//                 ...updatedProperties,
//             };
//             localStorage.setItem(storageKey, JSON.stringify(updatedData));
//         }
//     } catch (error) {
//         console.log("→ [LOG] Error updating local storage", error);
//     }
// };

// const _saveTestInLocalStorage = (
//     updatedProperties: Partial<TeacherTestDetails>,
//     createPreviewData?: () => StudentTestContent
// ) => {
//     _updateLocalStorage("localPreviewData", updatedProperties, createPreviewData ? updatedProperties : undefined);

//     const overwritePreviewData = createPreviewData ? createPreviewData() : undefined;
//     _updateLocalStorage("localFullPreviewData", updatedProperties, overwritePreviewData);
// };

// _____________________________________________________________________________________________________
